import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchActivePlans } from '../../../api/plans.api';
import { updateSubscription, type AdminSubscription } from '../../../api/subscriptions.api';
import type { SubscriptionStatusValue } from '../../../types/subscription.types';
import type { AdminPlan } from '../../../types/plan.types';

type Props = {
  open: boolean;
  subscription: AdminSubscription | null;
  onClose: () => void;
  onSaved: () => void;
};

const STATUS_OPTIONS: SubscriptionStatusValue[] = ['active', 'pending', 'expired', 'cancelled'];

function toInputDate(value?: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

export default function EditSubscriptionModal({ open, subscription, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [planId, setPlanId] = useState('');
  const [status, setStatus] = useState<SubscriptionStatusValue>('active');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !subscription) return;
    setPlanId(String(subscription.plan_id ?? ''));
    const st = subscription.status as SubscriptionStatusValue;
    setStatus(STATUS_OPTIONS.includes(st) ? st : 'active');
    setStartsAt(toInputDate(subscription.starts_at));
    setEndsAt(toInputDate(subscription.ends_at));
    setError('');
    setSaving(false);
    void fetchActivePlans().then((r) => {
      if (r.ok) setPlans(r.plans);
    });
  }, [open, subscription]);

  if (!open || !subscription) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const result = await updateSubscription(subscription.id, {
      plan_id: planId ? Number(planId) : undefined,
      status,
      starts_at: startsAt || null,
      ends_at: endsAt || null,
    });
    setSaving(false);
    if (result.ok === false) {
      setError(result.message);
      return;
    }
    onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h3 className="text-base font-bold text-gray-900">{t('subscriptions.edit.title')}</h3>
            <p className="mt-0.5 text-xs text-gray-400">
              {subscription.tenant?.name ?? subscription.tenant_id}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4 px-5 py-4">
          {error ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-100">{error}</div>
          ) : null}

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-gray-600">{t('subscriptions.edit.plan')}</label>
            <select
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
            >
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title || p.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-gray-600">{t('subscriptions.edit.status')}</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as SubscriptionStatusValue)}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {t(`status.${s}`, { defaultValue: s })}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-gray-600">{t('subscriptions.col.starts_at')}</label>
              <input
                type="date"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-gray-600">{t('subscriptions.col.ends_at')}</label>
              <input
                type="date"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 cursor-pointer">
              {t('subscriptions.edit.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-50 cursor-pointer"
            >
              {saving ? t('subscriptions.edit.saving') : t('subscriptions.edit.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
